/* eslint-disable max-lines */
import _ from 'lodash';
import React from 'react';
import QueryString from 'query-string';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Text, { Inline } from 'components/base/Text';
import { Flex, Padding, Separator } from 'components/base/layout-components';
import { SIZES } from 'common/constants/sizes';
import StyledAvatar from 'components/StyledAvatar';
import WorkBreakdownProgress from 'components/WorkBreakdown/WorkBeakdownProgress';
import { TooltipList } from 'components/TooltipList';
import { numeralify } from 'common/utils/utils';
import {
  getDurationLevels,
  getDefaultFormatter
} from 'common/utils/time.utils';
import AppRouter from 'common/AppRouter';
import { getColor } from 'common/constants/_colors';
import { CycleTimePopupTooltip } from 'components/Tooltips/CycleTimeTooltip';
import { mapCycleTimeStatisticsToPipelineMilestones } from 'components/DeliveryPipeline';
import { MEDIAN_METRICS, metricNames } from '@linearb/linjs-common';
import { WorkBreakdownPopupTooltip } from 'components/Tooltips/WorkBreakdownTooltip';
import {
  calcWorkBreakDownData,
  getWorkBreakdownConfigurations
} from '../../../dashboard/util';
import { getCycleTimeConfigurations } from 'views/root/views/home/views/dashboard/dashboard.service';
import Icon from 'components/base/Icon';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import moment from 'moment';

const LinkWithoutUnderline = styled(Link)`
  text-decoration: none;
  color: ${getColor('black')};
`;

export const columns = [
  {
    dataPoint: 'contributor',
    name: 'MEMBERS',
    isSortedCol: false,
    tableCellProps: {
      style: {
        minWidth: '10rem'
      }
    }
  },
  {
    dataPoint: metricNames.ACTIVE_BRANCHES,
    name: 'WIP',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0 } }
  },
  {
    dataPoint: metricNames.DONE_BRANCHES,
    name: 'DONE',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.ACTIVE_DAYS,
    name: 'ACTIVE DAYS',
    isSortedCol: true,
    tableCellProps: { style: { borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.COMMIT_CODE_CHANGES,
    name: 'CODE CHANGES',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, width: '9%' } }
  },
  {
    dataPoint: metricNames.COMMIT_INVOLVED_REPOS_COUNT,
    name: 'REPOSITORIES',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.CYCLE_TIME,
    name: 'CYCLE TIME (AVG)',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: 'workBreakdown',
    name: 'WORK BREAKDOWN',
    isSortedCol: false,
    tableCellProps: { style: { borderLeft: 0, minWidth: '9rem' } }
  },
  {
    dataPoint: metricNames.PR_NEW,
    name: 'PRs OPENED',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0 } }
  },
  {
    dataPoint: metricNames.REVIEWS,
    name: 'REVIEWS',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.REVIEW_DEPTH,
    name: 'REVIEW DEPTH',
    isSortedCol: true,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  }
];

// eslint-disable-next-line max-lines-per-function
export const getContributorsTableData = ({
  data,
  time: { from, to },
  timeRangeIteration,
  gitIntegrations = [],
  contributors,
  selectedTeamId,
  metricsCalculationStrategy,
  benchmarkOrgSize
}) => {
  return data.map(
    // eslint-disable-next-line max-lines-per-function
    curItem => {
      MEDIAN_METRICS.forEach(medianMetric => {
        curItem[medianMetric] =
          curItem[`${medianMetric}:${metricsCalculationStrategy}`];
      });
      const {
        contributor_id,
        [metricNames.ACTIVE_BRANCHES]: activeBranchesCount = '-',
        [metricNames.DONE_BRANCHES]: doneBranchesCount = '-',
        [metricNames.CYCLE_TIME]: cycleTime = 0,
        [metricNames.REVIEWS]: pullRequestReviewCount = '-',
        [metricNames.REVIEW_DEPTH]: pullRequestReviewDepth = null,
        [metricNames.ACTIVE_DAYS]: activeDay = '-',
        [metricNames.COMMIT_CODE_CHANGES]: changes = '-',
        [metricNames.COMMIT_INVOLVED_REPOS_COUNT]: involvedRepos = '-',
        [metricNames.NEW_WORK]: commitNewWork = 0,
        [metricNames.REWORK]: commitRework = 0,
        [metricNames.REFACTOR]: commitRefactor = 0,
        [metricNames.PR_NEW]: prNew = '-',
        avatarUrl,
        name,
        email,
        username,
        team = {}
      } = curItem;
      const avatar = {
        url: avatarUrl,
        name: email,
        id: contributor_id
      };
      const cycleTimeDisplay = cycleTime
        ? {
            value: getDurationLevels({
              minutes: cycleTime || 0,
              format: getDefaultFormatter({ minutes: cycleTime || 0 }),
              ignoreZeros: true
            })
          }
        : {};

      const search = QueryString.parse(window.location.search, {
        ignoreQueryPrefix: true
      });
      const styles = team.hasLeftOnly ? { grayedOut: true } : {};
      const baseParams = {
        ...(search.teams ? { teams: search.teams } : {}),
        iteration_id: search.iteration_id,
        contributors: contributor_id
      };

      const contribToLink = {
        pathname: AppRouter.BRANCH.path,
        search: QueryString.stringify({
          ...baseParams,
          time_range: [
            moment(from).format(SEARCH_DATE_FORMAT),
            moment.min(moment(to), moment()).format(SEARCH_DATE_FORMAT)
          ].join(','),
          metric_name: metricNames.ACTIVE_BRANCHES
        })
      };

      const currentContributor =
        contributors.find(contributor => contributor?.id === contributor_id) ||
        {};
      const isParent = _.get(currentContributor, 'is_parent', false);

      const childIds = _.get(currentContributor, 'contained_ids', []);
      const currentIntegration = _.get(
        currentContributor,
        'sensor_authorization_id',
        ''
      );
      const currentIntegrationName = _.get(
        gitIntegrations.find(({ id }) => id === currentIntegration),
        'name',
        ''
      );
      const tooltipTitle = (
        <Link
          style={{
            textDecoration: 'none',
            color: `${getColor('chambray')}`,
            fontSize: SIZES.medium
          }}
          to={contribToLink}
        >
          View {name ? name.split(' ', 1)[0] : username}’s active branches
        </Link>
      );
      const tooltipItems = [
        // eslint-disable-next-line react/jsx-key
        <Text size='large' weight={500} pb={isParent ? '0.3rem' : '1rem'}>
          {name ? name : username}
        </Text>,
        isParent ? (
          <Padding b='small'>
            <Text size='medium' color='osloGray'>{`${
              childIds.length + 1
            } Git accounts`}</Text>
          </Padding>
        ) : null,
        // eslint-disable-next-line react/jsx-key
        <Separator width='100%' height='1px' margin='0 0 1rem 0'></Separator>,
        team.info ? (
          <Padding b='normal'>
            <Text size='medium' color='osloGray' pb='0.3rem'>
              {team.info}
            </Text>
            <Text size='medium'>{(team.tooltipItems || []).join(', ')}</Text>
          </Padding>
        ) : null,
        gitIntegrations.length > 1 ? (
          <Padding b='normal'>
            <Text size='medium' color='osloGray' pb='0.3rem'>
              In integration:
            </Text>
            <Text size='medium'>{currentIntegrationName}</Text>
          </Padding>
        ) : null
      ].filter(Boolean);
      const isSyncedContributor = team?.syncedTeams?.length > 0;
      return {
        styles,
        contributor: (
          <TooltipList title={tooltipTitle} tooltipItems={tooltipItems}>
            <Flex direction='row' items='center'>
              <StyledAvatar
                avatar={avatar}
                disableTooltip
                changeCursor
                isSynced={isSyncedContributor}
              />
              <Flex pl='small' direction='column' items='left'>
                <Flex items='center'>
                  <Inline
                    pr={isParent ? '0.5rem' : ''}
                    size='large'
                    weight={400}
                    multiLineEllipsisAfter={1}
                  >
                    {name ? name : username}{' '}
                  </Inline>
                  {isParent && (
                    <Icon name='MergedUsers' color='osloGray' size='small' />
                  )}
                </Flex>
                {email && (
                  <Inline size='small' weight={400} color='gray'>
                    {email}
                  </Inline>
                )}
                {team.info && (
                  <Inline size='small' weight={400} color='gray'>
                    {team.info}
                  </Inline>
                )}
              </Flex>
            </Flex>
          </TooltipList>
        ),
        [metricNames.ACTIVE_BRANCHES]: (
          <LinkWithoutUnderline
            to={{
              pathname: AppRouter.BRANCH.path,
              search: QueryString.stringify({
                ...baseParams,
                metric_name: metricNames.ACTIVE_BRANCHES,
                time_range: [
                  moment(from).format(SEARCH_DATE_FORMAT),
                  moment.min(moment(to), moment()).format(SEARCH_DATE_FORMAT)
                ].join(',')
              })
            }}
          >
            <Text cursor='pointer' size='large'>
              {' '}
              {activeBranchesCount}{' '}
            </Text>
          </LinkWithoutUnderline>
        ),
        [metricNames.DONE_BRANCHES]: (
          <LinkWithoutUnderline
            to={{
              pathname: AppRouter.BRANCH.path,
              search: QueryString.stringify({
                ...baseParams,
                metric_name: metricNames.DONE_BRANCHES,
                time_range: [
                  moment(from).format(SEARCH_DATE_FORMAT),
                  moment.min(moment(to), moment()).format(SEARCH_DATE_FORMAT)
                ].join(',')
              })
            }}
          >
            <Text cursor='pointer' size='large'>
              {' '}
              {doneBranchesCount}{' '}
            </Text>
          </LinkWithoutUnderline>
        ),
        [metricNames.CYCLE_TIME]: (
          <CycleTimePopupTooltip
            title={
              <Text weight='500' size='x-large'>
                {'Cycle time breakdown'}
              </Text>
            }
            scale={0.9}
            configurations={getCycleTimeConfigurations(
              mapCycleTimeStatisticsToPipelineMilestones(
                curItem,
                {},
                { metricsCalculationStrategy },
                benchmarkOrgSize
              ),
              true
            )}
            totalTime={
              _.get(curItem, metricNames.REVIEW_TIME, null) !== null
                ? getDurationLevels({
                    minutes: cycleTime,
                    ignoreZeros: true,
                    format: getDefaultFormatter({
                      minutes: cycleTime,
                      minutesUnit: ' minutes',
                      daysUnit: ' days',
                      hoursUnit: ' hours',
                      yearsUnit: ' years'
                    }),
                    joiner: ' '
                  })
                : 'N/A'
            }
          >
            <LinkWithoutUnderline
              to={{
                pathname: AppRouter.BRANCH.path,
                search: QueryString.stringify({
                  ...baseParams,
                  metric_name: metricNames.CYCLE_TIME,
                  cycle_time_updated: `${from},${to}`,
                  order_by: 'cycle_time,desc'
                })
              }}
            >
              <Text size='large' cursor='pointer'>
                {' '}
                {_.get(cycleTimeDisplay, 'value', '-')}{' '}
              </Text>
            </LinkWithoutUnderline>
          </CycleTimePopupTooltip>
        ),
        [metricNames.REVIEWS]: (
          <Text size='large'> {pullRequestReviewCount} </Text>
        ),
        [metricNames.REVIEW_DEPTH]: (
          <Text size='large'>
            {' '}
            {pullRequestReviewDepth !== null
              ? Math.ceil(pullRequestReviewDepth * 10) / 10
              : '-'}{' '}
          </Text>
        ),
        [metricNames.ACTIVE_DAYS]: <Text size='large'> {activeDay} </Text>,
        [metricNames.COMMIT_CODE_CHANGES]: (
          <Text size='large'> {numeralify(changes)} </Text>
        ),
        [metricNames.COMMIT_INVOLVED_REPOS_COUNT]: (
          <Text size='large'> {involvedRepos} </Text>
        ),
        [metricNames.PR_NEW]: (
          <LinkWithoutUnderline
            to={{
              pathname: AppRouter.PULL_REQUESTS.path,
              search: QueryString.stringify({
                ...(search.teams ? { teams: search.teams } : {}),
                iteration_id: search.iteration_id,
                authors: contributor_id,
                created: `${from},${to}`
              })
            }}
          >
            <Text cursor='pointer' size='large'>
              {' '}
              {prNew}{' '}
            </Text>
          </LinkWithoutUnderline>
        ),
        workBreakdown: (
          <WorkBreakdownPopupTooltip
            title={
              <Text weight='500' size='x-x-large'>
                Work breakdown
              </Text>
            }
            scale={0.75}
            configurations={getWorkBreakdownConfigurations(
              calcWorkBreakDownData({
                newWork: commitNewWork,
                rework: commitRework,
                refactor: commitRefactor,
                changes
              })
            )}
          >
            <Padding b='small'>
              <WorkBreakdownProgress
                newWork={commitNewWork}
                refactor={commitRefactor}
                rework={commitRework}
                changes={changes}
              />
            </Padding>
          </WorkBreakdownPopupTooltip>
        )
      };
    }
  );
};
